import { useState, useEffect, useMemo, useRef } from 'react';
import { api } from '../api/client';
import { useT } from '../i18n';

interface GapCapability {
  id: number;
  title: string;
  description: string;
  domain: string;
  nature: string;
  avgMaturity: number;
  avgCriticality: number;
}

interface GapResource {
  id: number;
  title: string;
  description: string;
  type: string;
  function: string;
  appearances: number;
  avgMaturity: number;
  maxGap: number;
}

interface GapCell {
  criticality: number;
  maturity: number;
  gap: number;
}

interface GapMatrix {
  capabilities: GapCapability[];
  resources: GapResource[];
  matrix: Record<string, GapCell>;
}

type SortMode = 'frequency' | 'worst-gap' | 'lowest-maturity';

const GAP_LEVELS = [
  { key: 'critical', min: 0.50, color: '#d32f2f' },
  { key: 'high', min: 0.40, color: '#e67700' },
  { key: 'medium', min: 0.30, color: '#f59f00' },
  { key: 'low', min: 0.20, color: '#74b816' },
  { key: 'ok', min: 0, color: '#2b8a3e' },
];

function getGapColor(gap: number): string {
  for (const level of GAP_LEVELS) {
    if (gap >= level.min) return level.color;
  }
  return GAP_LEVELS[GAP_LEVELS.length - 1].color;
}

function getGapTextColor(gap: number): string {
  return gap >= 0.40 ? '#fff' : '#1a1a1a';
}

function InfoTooltip({
  item,
  position,
  onClose,
}: {
  item: { title: string; description: string };
  position: { x: number; y: number };
  onClose: () => void;
}) {
  const { t } = useT();
  const tooltipRef = useRef<HTMLDivElement>(null);

  const style: React.CSSProperties = {
    left: Math.min(position.x, window.innerWidth - 320),
    top: Math.min(position.y, window.innerHeight - 200),
  };

  return (
    <>
      <div className="info-tooltip-overlay" onClick={onClose} />
      <div className="info-tooltip" ref={tooltipRef} style={style}>
        <div className="info-tooltip-title">{item.title}</div>
        <div className="info-tooltip-description">
          {item.description || t('gap.noDescription')}
        </div>
      </div>
    </>
  );
}

export function GapAnalysisPage() {
  const { t } = useT();
  const [data, setData] = useState<GapMatrix | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCaps, setSelectedCaps] = useState<Set<number>>(new Set());
  const [selectedRes, setSelectedRes] = useState<Set<number>>(new Set());
  const [sortMode, setSortMode] = useState<SortMode>('frequency');
  const [capDropdownOpen, setCapDropdownOpen] = useState(false);
  const [resDropdownOpen, setResDropdownOpen] = useState(false);
  const [tooltipItem, setTooltipItem] = useState<{ title: string; description: string } | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const result = await api.getGapAnalysisMatrix() as GapMatrix;
      setData(result);
      setSelectedCaps(new Set(result.capabilities.map(c => c.id)));
      setSelectedRes(new Set(result.resources.map(r => r.id)));
    } catch (err: any) {
      setError(err.message || t('gap.error.load'));
    } finally {
      setLoading(false);
    }
  };

  const filteredCaps = useMemo(() => {
    if (!data) return [];
    return data.capabilities.filter(c => selectedCaps.has(c.id));
  }, [data, selectedCaps]);

  const filteredResources = useMemo(() => {
    if (!data) return [];
    let resources = data.resources.filter(r => selectedRes.has(r.id));

    switch (sortMode) {
      case 'worst-gap':
        resources = [...resources].sort((a, b) => b.maxGap - a.maxGap);
        break;
      case 'lowest-maturity':
        resources = [...resources].sort((a, b) => a.avgMaturity - b.avgMaturity);
        break;
      case 'frequency':
      default:
        resources = [...resources].sort((a, b) => b.appearances - a.appearances);
        break;
    }

    return resources;
  }, [data, selectedRes, sortMode]);

  const handleShowTooltip = (item: { title: string; description: string }, e: React.MouseEvent) => {
    setTooltipItem(item);
    setTooltipPosition({ x: e.clientX + 10, y: e.clientY + 10 });
  };

  if (loading) {
    return (
      <div className="page-padded" style={{ display: 'flex', justifyContent: 'center', paddingTop: '4rem' }}>
        <div className="spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-padded">
        <div className="alert alert-error">{error}</div>
      </div>
    );
  }

  if (!data || data.capabilities.length === 0) {
    return (
      <div className="page-padded">
        <div className="detail-card" style={{ textAlign: 'center', padding: '3rem' }}>
          <h2 style={{ marginBottom: 'var(--s8)', color: 'var(--ink-2)' }}>{t('gap.empty.title')}</h2>
          <p style={{ color: 'var(--ink-3)' }}>
            {t('gap.empty.desc')}
          </p>
        </div>
      </div>
    );
  }

  const toggleCap = (id: number) => {
    setSelectedCaps(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleRes = (id: number) => {
    setSelectedRes(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allCapsSelected = selectedCaps.size === data.capabilities.length;
  const allResSelected = selectedRes.size === data.resources.length;

  return (
    <div className="page-padded gap-analysis-page">
      {/* Header */}
      <div style={{ marginBottom: 'var(--s16)' }}>
        <h1 className="page-title">{t('gap.title')}</h1>
        <p style={{ color: 'var(--ink-3)', fontSize: 'var(--t-13)', marginTop: 'var(--s4)' }}>
          {t('gap.subtitle')}
        </p>
      </div>

      {/* Legend */}
      <div className="gap-legend">
        {GAP_LEVELS.map(level => (
          <div key={level.key} className="gap-legend-item">
            <span className="gap-legend-swatch" style={{ background: level.color }} />
            <span className="gap-legend-label">{t(`gap.level.${level.key}`)} ({level.min === 0 ? t('gap.legend.lowest') : `≥${level.min.toFixed(2)}`})</span>
          </div>
        ))}
      </div>

      {/* Filter Bar */}
      <div className="gap-filter-bar">
        {/* Capabilities filter */}
        <div className="gap-filter-dropdown">
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => { setCapDropdownOpen(!capDropdownOpen); setResDropdownOpen(false); }}
          >
            {t('gap.filter.capabilities')} ({selectedCaps.size}/{data.capabilities.length})
          </button>
          {capDropdownOpen && (
            <div className="gap-dropdown-menu">
              <label className="gap-dropdown-item gap-dropdown-item--header">
                <input
                  type="checkbox"
                  checked={allCapsSelected}
                  onChange={() => setSelectedCaps(allCapsSelected ? new Set() : new Set(data.capabilities.map(c => c.id)))}
                />
                <span>{t('gap.filter.all')}</span>
              </label>
              {data.capabilities.map(c => (
                <label key={c.id} className="gap-dropdown-item">
                  <input type="checkbox" checked={selectedCaps.has(c.id)} onChange={() => toggleCap(c.id)} />
                  <span>{c.title}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Resources filter */}
        <div className="gap-filter-dropdown">
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => { setResDropdownOpen(!resDropdownOpen); setCapDropdownOpen(false); }}
          >
            {t('gap.filter.resources')} ({selectedRes.size}/{data.resources.length})
          </button>
          {resDropdownOpen && (
            <div className="gap-dropdown-menu">
              <label className="gap-dropdown-item gap-dropdown-item--header">
                <input
                  type="checkbox"
                  checked={allResSelected}
                  onChange={() => setSelectedRes(allResSelected ? new Set() : new Set(data.resources.map(r => r.id)))}
                />
                <span>{t('gap.filter.all')}</span>
              </label>
              {data.resources.map(r => (
                <label key={r.id} className="gap-dropdown-item">
                  <input type="checkbox" checked={selectedRes.has(r.id)} onChange={() => toggleRes(r.id)} />
                  <span>{r.title}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Sort buttons */}
        <div className="gap-sort-group">
          <span style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)', marginRight: 'var(--s4)' }}>{t('gap.sort.label')}</span>
          {(['frequency', 'worst-gap', 'lowest-maturity'] as SortMode[]).map(mode => (
            <button
              key={mode}
              className={`btn btn-ghost btn-sm gap-sort-btn ${sortMode === mode ? 'active' : ''}`}
              onClick={() => setSortMode(mode)}
            >
              {mode === 'frequency' ? t('gap.sort.frequency') : mode === 'worst-gap' ? t('gap.sort.worstGap') : t('gap.sort.lowestMaturity')}
            </button>
          ))}
        </div>
      </div>

      {/* Close dropdowns on click outside */}
      {(capDropdownOpen || resDropdownOpen) && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 9 }}
          onClick={() => { setCapDropdownOpen(false); setResDropdownOpen(false); }}
        />
      )}

      {/* Heatmap Table */}
      {filteredResources.length === 0 || filteredCaps.length === 0 ? (
        <div className="detail-card" style={{ textAlign: 'center', padding: '2rem', color: 'var(--ink-3)' }}>
          {t('gap.noMatch')}
        </div>
      ) : (
        <div className="gap-grid-container">
          <table className="gap-grid">
            <thead>
              <tr>
                <th className="gap-grid-corner">{t('gap.col.resource')}</th>
                {filteredCaps.map(cap => (
                  <th
                    key={cap.id}
                    className="gap-grid-col-header"
                    onClick={(e) => handleShowTooltip(cap, e)}
                  >
                    <div className="gap-col-title">{cap.title}</div>
                    <div className="gap-cap-health">
                      <span className="badge badge-sm" title={t('gap.tooltip.avgMaturity').replace('{n}', cap.avgMaturity.toFixed(1))}>
                        {t('gap.abbr.maturity')} {cap.avgMaturity.toFixed(1)}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredResources.map(res => (
                <tr key={res.id} className="gap-grid-row">
                  <td
                    className="gap-grid-row-header"
                    onClick={(e) => handleShowTooltip(res, e)}
                  >
                    <span className="gap-resource-name">{res.title}</span>
                    <span className="gap-resource-count" title={t('gap.tooltip.appearsIn').replace('{n}', String(res.appearances))}>
                      ×{res.appearances}
                    </span>
                  </td>
                  {filteredCaps.map(cap => {
                    const key = `${res.id}:${cap.id}`;
                    const cell = data.matrix[key];

                    if (!cell) {
                      return (
                        <td key={cap.id} className="gap-cell gap-cell--empty">
                          <span className="gap-cell-dash">—</span>
                        </td>
                      );
                    }

                    const bgColor = getGapColor(cell.gap);
                    const textColor = getGapTextColor(cell.gap);

                    return (
                      <td
                        key={cap.id}
                        className="gap-cell"
                        style={{ background: bgColor, color: textColor }}
                        title={t('gap.tooltip.cell').replace('{gap}', cell.gap.toFixed(2)).replace('{crit}', cell.criticality.toFixed(2)).replace('{mat}', cell.maturity.toFixed(1))}
                      >
                        <span className="gap-cell-score">{cell.gap.toFixed(2)}</span>
                        <span className="gap-cell-detail">
                          {t('gap.abbr.criticality')} {cell.criticality.toFixed(2)} · {t('gap.abbr.maturity')} {cell.maturity.toFixed(1)}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Info Tooltip */}
      {tooltipItem && (
        <InfoTooltip
          item={tooltipItem}
          position={tooltipPosition}
          onClose={() => setTooltipItem(null)}
        />
      )}
    </div>
  );
}
